import React, { MouseEvent, useState, useCallback, useEffect } from 'react';
import FieldInfo from '../field-info/FieldInfo';
import FieldActionMenu from '../field-action-menu/FieldActionMenu';
import { ReportField } from '../../../models/reportField';
import {
  deleteFavoriteFieldsWithClearCache,
  fetchFavoriteFieldsInMapWithCache,
  postFavoriteFieldsWithClearCache,
} from '../../../api';
import { useAuth } from '../../../context';
import { createReportFieldKey } from '../../../util';

interface FieldPopoverContentProps {
  field: ReportField;
  onAddToRuleBuilderClick?: (e: MouseEvent) => void;
  onAddedToFavorite?: (field: ReportField) => void;
  onDeletedFromFavorite?: (field: ReportField) => void;
}

function FieldPopoverContent({
  field,
  onAddToRuleBuilderClick,
  onAddedToFavorite,
  onDeletedFromFavorite,
}: FieldPopoverContentProps): JSX.Element {
  const { client } = useAuth();
  const [isAddedFavorite, setIsAddedFavorite] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const addToFavoriteClickHandler = useCallback(async () => {
    setIsLoading(true);
    await postFavoriteFieldsWithClearCache({
      ClientId: client.clientId,
      FieldId: field.id,
      IsClientCustom: field.isClientCustom,
      SubFieldId: field.subFieldId,
      IsAllocation: field.isAllocation,
      IsDimension: field.isDimension,
      IsDimensionItemId: field.isDimensionItemId,
    });
    setIsAddedFavorite(true);
    onAddedToFavorite(field);
    setIsLoading(false);
  }, [client.clientId, field, onAddedToFavorite]);

  const removeFromFavoriteClickHandler = useCallback(async () => {
    setIsLoading(true);
    await deleteFavoriteFieldsWithClearCache(
      field.id,
      field.isClientCustom,
      field.subFieldId,
      field.isDimension,
      field.isDimensionItemId,
      field.isAllocation,
    );
    setIsAddedFavorite(false);
    onDeletedFromFavorite(field);
    setIsLoading(false);
  }, [field, onDeletedFromFavorite]);

  useEffect(() => {
    setIsLoading(true);
    const checkFieldIsAddedToFavorite = async (): Promise<void> => {
      const favoriteFieldsMap = await fetchFavoriteFieldsInMapWithCache(
        client.clientId,
      );
      const fieldKey = createReportFieldKey(field);
      setIsAddedFavorite(favoriteFieldsMap.has(fieldKey));
      setIsLoading(false);
    };
    checkFieldIsAddedToFavorite();
  }, [client.clientId, field]);

  return (
    <>
      <FieldInfo isAddedFavorite={isAddedFavorite} field={field} />
      <FieldActionMenu
        isAddedFavorite={isAddedFavorite}
        isLoading={isLoading}
        onAddToRuleBuilderClick={onAddToRuleBuilderClick}
        onAddToFavoriteClick={addToFavoriteClickHandler}
        onRemoveFromFavoriteClick={removeFromFavoriteClickHandler}
      />
    </>
  );
}

export default FieldPopoverContent;
